import { getMixedPalette } from "design-system/lib/chart-utils"
import { ChartContainer, ChartTooltip } from "design-system/molecules/Chart"
import { AnimatePresence, motion } from "framer-motion"
import { ensure } from "lopermut"
import { UserRoundX } from "lucide-react"
import { usePredefinedSegmentColors } from "modules/segments/hooks/usePredefinedSegmentColors"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { Loading } from "modules/shared/components/Loading"
import React, { useMemo } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis } from "recharts"
import {
  PredefinedSegmentGroups,
  SegmentGroupComparisonResponseDataSchema,
  SegmentGroupResponseDataSchema,
  UnknownSegmentName,
} from "schema"
import z from "zod"
import {
  SegmentGroupChartTooltipOverlay,
  SegmentGroupTooltipData,
} from "./SegmentGroupChartTooltipOverlay"

const COLORS = getMixedPalette(false, true)

type Props = {
  segmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>
  comparisonData?: z.infer<typeof SegmentGroupComparisonResponseDataSchema>
  comparisonSegmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>
  isPending: boolean
}

export function SegmentGroupChart({
  segmentGroup,
  comparisonData,
  comparisonSegmentGroup,
  isPending,
}: Props) {
  const { t } = useTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const predefinedSegmentColors = usePredefinedSegmentColors()

  const hideTimerRef = React.useRef<number | null>(null)
  const chartContainerRef = React.useRef<HTMLDivElement | null>(null)
  const [tooltip, setTooltip] = React.useState<SegmentGroupTooltipData | null>(null)
  const [isTooltipHovered, setIsTooltipHovered] = React.useState(false)
  const tooltipPositionRef = React.useRef<{ x: number; y: number } | null>(null)
  const currentBarRef = React.useRef<string | null>(null)
  const mousePositionRef = React.useRef<{ x: number; y: number } | null>({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  })

  React.useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
    }
  }, [])

  // Clear tooltip state when segment group changes
  React.useEffect(() => {
    setTooltip(null)
    tooltipPositionRef.current = null
    currentBarRef.current = null
  }, [segmentGroup?.id, comparisonSegmentGroup?.id])

  const handleTooltipChange = React.useCallback(
    (tooltipData: SegmentGroupTooltipData | null) => {
      if (tooltipData === null) {
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = window.setTimeout(() => {
          if (!isTooltipHovered) {
            setTooltip(null)
            tooltipPositionRef.current = null
            currentBarRef.current = null
          }
        }, 120)
      } else {
        if (hideTimerRef.current) {
          window.clearTimeout(hideTimerRef.current)
          hideTimerRef.current = null
        }

        const currentBar = tooltipData.payload.name

        // Update position only if hovering over a different bar
        if (currentBarRef.current !== currentBar) {
          tooltipPositionRef.current = {
            x: tooltipData.coordinate.x,
            y: tooltipData.coordinate.y,
          }
          currentBarRef.current = currentBar
        }

        // Use the stored position for consistent placement
        const newTooltipData = {
          ...tooltipData,
          coordinate: tooltipPositionRef.current!,
        }

        // Only update if data actually changed
        setTooltip((prev) => {
          if (
            !prev ||
            prev.payload.name !== newTooltipData.payload.name ||
            prev.coordinate.x !== newTooltipData.coordinate.x ||
            prev.coordinate.y !== newTooltipData.coordinate.y
          ) {
            return newTooltipData
          }
          return prev
        })
      }
    },
    [isTooltipHovered],
  )

  const handleTooltipMouseEnter = React.useCallback(() => {
    setIsTooltipHovered(true)
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const handleTooltipMouseLeave = React.useCallback(() => {
    setIsTooltipHovered(false)
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
    hideTimerRef.current = window.setTimeout(() => setTooltip(null), 120)
  }, [])

  const { chartData, allDataKeys, colorMap, hasComparison } = useMemo(() => {
    const segments = segmentGroup?.segments ?? []
    if (segments.length === 0) {
      return { chartData: [], allDataKeys: [], colorMap: {}, hasComparison: false }
    }

    const hasComparisonData = comparisonData && comparisonData.length > 0
    const colorMapping: Record<string, string> = {}

    const allStackKeys: string[] = []
    if (hasComparisonData && comparisonData) {
      comparisonData.forEach((compSegment) => {
        compSegment.overlappingSegments.forEach((overlap) => {
          const key = `overlap_${overlap.id}`
          if (!allStackKeys.includes(key)) {
            allStackKeys.push(key)
          }
        })
      })
      allStackKeys.push("base")
    }

    // Pre-build color mapping for overlapping segments
    if (hasComparisonData && comparisonData) {
      allStackKeys.forEach((key, index) => {
        if (!colorMapping[key]) {
          if (key === "base") {
            colorMapping[key] = "var(--chart-primary)"
          } else {
            // Extract the segment ID from the overlap key (e.g., "overlap_123" -> "123")
            const segmentId = key.replace("overlap_", "")
            const overlappingSegment = comparisonData
              ?.find((comp) => comp.overlappingSegments.some((o) => o.id === segmentId))
              ?.overlappingSegments.find((o) => o.id === segmentId)

            if (
              overlappingSegment &&
              comparisonSegmentGroup?.name === PredefinedSegmentGroups.satisfaction
            ) {
              // Use predefined color if it's a satisfaction segment
              const predefinedColor =
                predefinedSegmentColors[PredefinedSegmentGroups.satisfaction]?.[
                  overlappingSegment.name
                ]
              if (predefinedColor) {
                colorMapping[key] = `var(--${predefinedColor})`
                return
              }
            }

            // Use secondary color for unknown segments
            if (overlappingSegment?.name === UnknownSegmentName) {
              colorMapping[key] = "var(--secondary)"
              return
            }

            // Fallback to default colors if no predefined color is found
            colorMapping[key] = `var(--${ensure(COLORS[index % COLORS.length])})`
          }
        }
      })
    }

    const data = segments.map((segment) => {
      const segmentName = getSegmentTranslation(segment.name, segmentGroup)

      const dataPoint: Record<
        string,
        string | number | boolean | Record<string, string | number | boolean>[]
      > = {
        id: segment.id,
        rawName: segment.name,
        name: segmentName,
        value: segment.count,
        barValue: segment.count,
        hasOverlap: false,
        totalPercent: segment.count / (segmentGroup?.customersCount ?? 0),
        color:
          segmentGroup?.name === PredefinedSegmentGroups.satisfaction
            ? `var(--${predefinedSegmentColors[PredefinedSegmentGroups.satisfaction]?.[segment.name] ?? "chart-primary"})`
            : "var(--chart-primary)",
      }

      const comparisonSegment = comparisonData?.find((comp) => comp.name === segment.name)

      if (
        hasComparisonData &&
        comparisonSegment &&
        comparisonSegment.overlappingSegments.length > 0
      ) {
        dataPoint.hasOverlap = true
        const overlapsPresent = new Map(
          comparisonSegment.overlappingSegments.map((o) => [`overlap_${o.id}`, o.overlapCount]),
        )

        allStackKeys.forEach((key) => {
          if (overlapsPresent.has(key)) {
            const value = overlapsPresent.get(key)!
            dataPoint[key] = value
          } else if (key !== "base") {
            dataPoint[key] = 0
          }
        })

        dataPoint.overlappingSegments = comparisonSegment.overlappingSegments
        dataPoint.translatedOverlappingSegments = comparisonSegment.overlappingSegments.map((o) => {
          const overlapKey = `overlap_${o.id}`
          return {
            ...o,
            translatedName: getSegmentTranslation(o.name, ensure(comparisonSegmentGroup)),
            // Use the pre-calculated percentage from the backend
            overlapPercent: o.overlapPercentage ?? 0,
            color: colorMapping[overlapKey] || "var(--chart-1)",
          }
        })
      }
      return dataPoint
    })

    return {
      chartData: data,
      allDataKeys: hasComparisonData ? allStackKeys : ["barValue"],
      colorMap: colorMapping,
      hasComparison: hasComparisonData,
    }
  }, [
    segmentGroup,
    comparisonData,
    getSegmentTranslation,
    comparisonSegmentGroup,
    predefinedSegmentColors,
  ])

  const chartKey = useMemo(
    () => [segmentGroup?.id, comparisonSegmentGroup?.id].filter(Boolean).join("-"),
    [segmentGroup, comparisonSegmentGroup],
  )

  if (isPending) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  const count = chartData?.length ?? 3
  const hasData = chartData && chartData.length > 0

  const labelListComponent = (
    <LabelList
      dataKey="value"
      position="right"
      offset={12}
      className="fill-foreground"
      fontSize={14}
      fontWeight={500}
      fontFamily="var(--font-mono)"
    />
  )

  return (
    <div className="flex-1" style={{ height: `${Math.max(count * 48, 260)}px` }}>
      <AnimatePresence mode="popLayout">
        <motion.div
          key={chartKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {hasData ? (
            <ChartContainer
              ref={chartContainerRef}
              config={{}}
              className="aspect-auto"
              style={{ height: `${Math.max(count * 48, 260)}px` }}
              onMouseMove={(e) => {
                mousePositionRef.current = { x: e.clientX, y: e.clientY }
              }}
            >
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, bottom: 5, left: 10 }}
              >
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length || !payload[0]) {
                      handleTooltipChange(null)
                      return null
                    }

                    handleTooltipChange({
                      payload: payload[0].payload,
                      coordinate: mousePositionRef.current || { x: 0, y: 0 },
                    })

                    return null
                  }}
                />
                <CartesianGrid horizontal={false} />
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  width={100}
                  tick={(props) => (
                    <g transform={`translate(${props.x},${props.y})`}>
                      <foreignObject
                        x={props.width ? -props.width : undefined}
                        y={-10}
                        width={props.width ?? 0}
                        height={22}
                      >
                        <p
                          className="truncate text-sm text-right text-muted-foreground"
                          title={props.payload.value}
                        >
                          {props.payload?.value}
                        </p>
                      </foreignObject>
                    </g>
                  )}
                />
                {hasComparison ? (
                  allDataKeys.map((key, index) => {
                    const isLastVisibleBar = index === allDataKeys.length - 1

                    return (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={colorMap[key]}
                        isAnimationActive={false}
                        barSize={42}
                      >
                        {isLastVisibleBar && labelListComponent}
                      </Bar>
                    )
                  })
                ) : (
                  <Bar
                    dataKey="barValue"
                    fill="var(--chart-primary)"
                    isAnimationActive={false}
                    barSize={42}
                  >
                    {chartData.map((entry, index) => {
                      return <Cell key={`cell-${index}`} fill={entry.color as string} />
                    })}
                    {labelListComponent}
                  </Bar>
                )}
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex flex-col gap-4 items-center justify-center min-h-[220px] text-center">
              <UserRoundX className="size-10" strokeWidth={1.25} />
              <div className="flex flex-col items-center">
                <span className="text-lg font-medium">{t("segments.list.empty.title")}</span>
                <span className="text-sm text-muted-foreground">
                  {t("segments.list.empty.description")}
                </span>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      {typeof document !== "undefined" && tooltip
        ? createPortal(
            <SegmentGroupChartTooltipOverlay
              segmentGroup={segmentGroup}
              tooltip={tooltip}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
            />,
            document.body,
          )
        : null}
    </div>
  )
}
